import axios from 'axios';
import { apiLogger } from '@/lib/logger';
import { supabase } from '@/services/supabaseClient';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
const DISABLE_AUTH = import.meta.env.VITE_DISABLE_AUTH === 'true';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30_000, // 30s — prevents requests hanging forever behind stuck browser connections
  headers: {
    'Content-Type': 'application/json',
  },
});

/** Raising-sized Management reads exceed the default 30s when structure,
 *  ads, and search terms run together. Search-term review already used
 *  120s; structure / ads / conversions / change-history inherit 30s and
 *  then Easy withholds Yes as "incomplete". Same budget for those reads. */
export const ADS_MANAGEMENT_READ_TIMEOUT_MS = 120_000;

/** Management writes that batch several Google Ads mutates (the Search Terms
 *  negatives apply: ~15-25 s batched, minutes before). Stays well under
 *  Railway's ~300 s request cut-off. */
export const ADS_MANAGEMENT_WRITE_TIMEOUT_MS = 120_000;

// Request interceptor — attach Supabase JWT if available
apiClient.interceptors.request.use(
  async (config) => {
    if (DISABLE_AUTH) {
      // Dev bypass: send a placeholder Bearer token so backend _check_auth passes
      config.headers.Authorization = 'Bearer dev-bypass-token';
    } else {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          config.headers.Authorization = `Bearer ${session.access_token}`;
        }
      } catch {
        // Auth may be disabled — continue without token
      }
    }

    (config as any).metadata = { startTime: performance.now() };

    apiLogger.debug(`${config.method?.toUpperCase()} ${config.url}`, {
      params: config.params,
      data: config.data ? '(has body)' : undefined,
    });
    return config;
  },
  (error) => {
    apiLogger.error('Request error', error);
    return Promise.reject(error);
  }
);

// Response interceptor — handle 401 with token refresh
apiClient.interceptors.response.use(
  (response) => {
    const duration = performance.now() - (response.config as any).metadata?.startTime;
    const url = response.config.url || '';

    apiLogger.debug(`${response.status} ${response.config.url}`, {
      dataType: typeof response.data,
      dataLength: Array.isArray(response.data) ? response.data.length : undefined,
    });

    // Performance logging for Command Center endpoints
    if (url.includes('/blog-creation/') || url.includes('/qa/')) {
      const size = JSON.stringify(response.data).length;
      apiLogger.info(`[PERF] ${response.config.method?.toUpperCase()} ${url} — ${duration.toFixed(0)}ms (${response.status})`, {
        duration: Math.round(duration),
        status: response.status,
        size,
      });

      if (duration > 500) {
        apiLogger.warn(`[PERF] SLOW REQUEST: ${response.config.method?.toUpperCase()} ${url} — ${duration.toFixed(0)}ms`, {
          duration: Math.round(duration),
          status: response.status,
          size,
        });
      }
    }

    return response;
  },
  async (error) => {
    const duration =
      performance.now() - ((error.config as any)?.metadata?.startTime ?? performance.now());
    const url = error.config?.url || '';
    if (url.includes('/blog-creation/') || url.includes('/qa/')) {
      apiLogger.warn(
        `[PERF] FAILED ${error.config?.method?.toUpperCase()} ${url} — ${Math.round(duration)}ms (${error.response?.status || 'network'})`,
        {
          duration: Math.round(duration),
          status: error.response?.status || 'network',
          error: error.response?.data?.detail || error.message,
        },
      );
    }

    const originalRequest = error.config;

    // On 401, try refreshing the token once
    if (error.response?.status === 401 && !originalRequest._retried) {
      originalRequest._retried = true;
      try {
        const { data: { session } } = await supabase.auth.refreshSession();
        if (session?.access_token) {
          originalRequest.headers.Authorization = `Bearer ${session.access_token}`;
          return apiClient(originalRequest);
        }
      } catch {
        // Refresh failed
      }
      // No valid session — let ProtectedRoute handle the redirect.
      // Do NOT use window.location.href = '/login' here — that causes a hard
      // page reload which creates an infinite loop when Supabase has a valid
      // session but the backend rejects the token (e.g., ES256 vs HS256 mismatch).
      return Promise.reject(error);
    }

    apiLogger.error(`API Error: ${error.response?.status || 'Network'}`, {
      url: error.config?.url,
      message: error.response?.data?.detail || error.message,
    });
    return Promise.reject(error);
  }
);
